import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Scale, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface WeightEstimatorProps {
  luggageTypes: string[];
  carryOnLimit: number;
  checkedLimit: number;
  weightUnit: 'kg' | 'lbs';
  airline: string;
}

interface WeightBreakdown {
  category: string;
  weight: number;
  percentage: number;
}

export function WeightEstimator({ 
  luggageTypes, 
  carryOnLimit, 
  checkedLimit, 
  weightUnit, 
  airline 
}: WeightEstimatorProps) {
  const [estimatedWeights, setEstimatedWeights] = useState({
    carryOn: 0,
    checked: 0,
    personal: 0
  });

  const [weightBreakdown, setWeightBreakdown] = useState<WeightBreakdown[]>([]);

  useEffect(() => {
    calculateEstimatedWeights();
  }, [luggageTypes]);

  const calculateEstimatedWeights = () => {
    // Base weight estimates in grams
    const baseWeights = {
      clothing: 2500, // ~2.5kg for basic clothing
      toiletries: 800, // ~0.8kg for toiletries
      electronics: 1200, // ~1.2kg for electronics
      documents: 100, // ~0.1kg for documents
      misc: 600 // ~0.6kg for misc items
    };

    // Convert to selected unit
    const convertWeight = (grams: number) => {
      return weightUnit === 'kg' ? grams / 1000 : (grams / 1000) * 2.20462;
    };

    // Estimate weights based on luggage types
    let carryOnWeight = 0;
    let checkedWeight = 0;
    let personalWeight = 0;

    if (luggageTypes.includes('carry-on')) {
      carryOnWeight = convertWeight(
        baseWeights.electronics + 
        baseWeights.documents + 
        baseWeights.toiletries * 0.3 + // Limited toiletries in carry-on
        baseWeights.clothing * 0.3 + // Some clothing in carry-on
        baseWeights.misc * 0.5
      );
    }

    if (luggageTypes.includes('checked')) {
      checkedWeight = convertWeight(
        baseWeights.clothing * 0.7 + // Most clothing in checked
        baseWeights.toiletries * 0.7 + // Most toiletries in checked
        baseWeights.misc * 0.5
      );
    }

    if (luggageTypes.includes('personal')) {
      personalWeight = convertWeight(
        baseWeights.electronics * 0.3 + // Some electronics in personal item
        baseWeights.documents * 0.5 + // Some documents in personal item
        200 // Personal item itself (bag, wallet, etc.)
      );
    }

    setEstimatedWeights({
      carryOn: Math.round(carryOnWeight * 10) / 10,
      checked: Math.round(checkedWeight * 10) / 10,
      personal: Math.round(personalWeight * 10) / 10
    });

    // Calculate breakdown
    const breakdown: WeightBreakdown[] = [];
    const totalWeight = carryOnWeight + checkedWeight + personalWeight;

    if (totalWeight > 0) {
      breakdown.push(
        { category: 'Clothing', weight: convertWeight(baseWeights.clothing), percentage: (convertWeight(baseWeights.clothing) / totalWeight) * 100 },
        { category: 'Electronics', weight: convertWeight(baseWeights.electronics), percentage: (convertWeight(baseWeights.electronics) / totalWeight) * 100 },
        { category: 'Toiletries', weight: convertWeight(baseWeights.toiletries), percentage: (convertWeight(baseWeights.toiletries) / totalWeight) * 100 },
        { category: 'Miscellaneous', weight: convertWeight(baseWeights.misc), percentage: (convertWeight(baseWeights.misc) / totalWeight) * 100 },
        { category: 'Documents', weight: convertWeight(baseWeights.documents), percentage: (convertWeight(baseWeights.documents) / totalWeight) * 100 }
      );
    }

    setWeightBreakdown(breakdown.sort((a, b) => b.weight - a.weight));
  };

  const getWeightStatus = (current: number, limit: number) => {
    if (limit === 0) return 'unknown';
    const percentage = (current / limit) * 100;
    if (percentage <= 80) return 'good';
    if (percentage <= 95) return 'warning';
    return 'danger';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'good':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'danger':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      default:
        return <Scale className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'good': return 'text-green-600';
      case 'warning': return 'text-yellow-600';
      case 'danger': return 'text-red-600';
      default: return 'text-muted-foreground';
    }
  };

  const getProgressColor = (status: string) => {
    switch (status) {
      case 'good': return 'bg-green-500';
      case 'warning': return 'bg-yellow-500';
      case 'danger': return 'bg-red-500';
      default: return 'bg-primary';
    }
  };

  if (luggageTypes.length === 0) {
    return (
      <Card className="border-cyan-500/20 shadow-md shadow-cyan-500/5 overflow-hidden">
        <CardHeader className="bg-gradient-to-br from-cyan-50/60 to-transparent dark:from-cyan-950/15 dark:to-transparent">
          <CardTitle className="flex items-center gap-3 text-base sm:text-lg">
            <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-cyan-400 to-teal-500 text-white shadow-sm shadow-cyan-500/20">
              <Scale className="h-4 w-4" />
            </span>
            Weight Estimator
          </CardTitle>
          <CardDescription className="pl-11">
            Select luggage types to see weight estimates
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center text-muted-foreground py-8">
            <Scale className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Choose your luggage configuration to get started</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-cyan-500/20 shadow-md shadow-cyan-500/5 overflow-hidden">
      <CardHeader className="bg-gradient-to-br from-cyan-50/60 to-transparent dark:from-cyan-950/15 dark:to-transparent">
        <CardTitle className="flex items-center gap-3 text-base sm:text-lg">
          <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-cyan-400 to-teal-500 text-white shadow-sm shadow-cyan-500/20">
            <Scale className="h-4 w-4" />
          </span>
          Weight Estimator
        </CardTitle>
        <CardDescription className="pl-11">
          Estimated weights to help avoid baggage fees
          {airline && ` • ${airline}`}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Luggage Weight Status */}
        <div className="space-y-4">
          {luggageTypes.includes('carry-on') && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {getStatusIcon(getWeightStatus(estimatedWeights.carryOn, carryOnLimit))}
                  <span className="font-medium">Carry-on</span>
                </div>
                <div className="text-right">
                  <span className={cn("font-medium", getStatusColor(getWeightStatus(estimatedWeights.carryOn, carryOnLimit)))}>
                    {estimatedWeights.carryOn} {weightUnit}
                  </span>
                  {carryOnLimit > 0 && (
                    <span className="text-muted-foreground text-sm"> / {carryOnLimit} {weightUnit}</span>
                  )}
                </div>
              </div>
              {carryOnLimit > 0 && (
                <Progress 
                  value={Math.min((estimatedWeights.carryOn / carryOnLimit) * 100, 100)} 
                  className="h-2"
                />
              )}
            </div>
          )}

          {luggageTypes.includes('checked') && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {getStatusIcon(getWeightStatus(estimatedWeights.checked, checkedLimit))}
                  <span className="font-medium">Checked bag</span>
                </div>
                <div className="text-right">
                  <span className={cn("font-medium", getStatusColor(getWeightStatus(estimatedWeights.checked, checkedLimit)))}>
                    {estimatedWeights.checked} {weightUnit}
                  </span>
                  {checkedLimit > 0 && (
                    <span className="text-muted-foreground text-sm"> / {checkedLimit} {weightUnit}</span>
                  )}
                </div>
              </div>
              {checkedLimit > 0 && (
                <Progress 
                  value={Math.min((estimatedWeights.checked / checkedLimit) * 100, 100)} 
                  className="h-2"
                />
              )}
            </div>
          )}

          {luggageTypes.includes('personal') && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Scale className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">Personal item</span>
                </div>
                <div className="text-right">
                  <span className="font-medium text-muted-foreground">
                    {estimatedWeights.personal} {weightUnit}
                  </span>
                  <span className="text-muted-foreground text-sm"> (no limit)</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Weight Breakdown */}
        {weightBreakdown.length > 0 && (
          <div className="space-y-3">
            <h4 className="font-medium text-sm">Weight Breakdown</h4>
            <div className="space-y-2">
              {weightBreakdown.map((item, index) => (
                <div key={item.category} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ 
                        backgroundColor: `hsl(${index * 60}, 65%, 50%)` 
                      }}
                    />
                    <span>{item.category}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">
                      {Math.round(item.weight * 10) / 10} {weightUnit}
                    </span>
                    <Badge variant="outline" className="text-xs">
                      {Math.round(item.percentage)}%
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tips */}
        <div className="p-3 bg-muted/30 rounded-lg">
          <p className="text-xs text-muted-foreground">
            💡 These are estimates based on typical packing. Actual weights may vary. 
            {carryOnLimit === 0 && checkedLimit === 0 && " Add weight limits for better accuracy."}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}